import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { observer } from "mobx-react-lite";
import { 
  ExportStore, 
  ExportType, 
  QualityLevel,
  VIDEO_QUALITY_PRESETS,
  AUDIO_QUALITY_PRESETS,
  RESOLUTION_PRESETS,
} from "../../librettoEditor/store/ExportStore";
import { ScriptLayerStore } from "../../librettoEditor/store/ScriptLayerStore";
import { CompositionStateStore } from "../../librettoEditor/store/CompositionStateStore";
import { DEFAULT_TRANSCRIPT_EXPORT_OPTIONS, type TranscriptExportOptions } from "./transcriptExport";

export interface ExportCardProps {
  onClose: () => void;
  onExport: (opts: { 
    tab: ExportType; 
    filename: string; 
    resolution?: { width: number; height: number };
    quality?: QualityLevel;
    transcriptOptions?: TranscriptExportOptions;
  }) => void | Promise<void>;
  defaultTitle?: string;
  durationSec?: number;
  canvasSize?: { width: number; height: number };
  anchorPosition?: { top: number; left: number };
  previewThumbnailUrl?: string;
  shouldStartTrialOnExport?: boolean;
  isStartingTrialOnExport?: boolean;
}

/**
 * Segmented control button
 */
const SegmentedButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      height: 28,
      px: 1,
      borderRadius: "10px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: active ? 500 : 400,
      color: active ? "var(--app-text-primary)" : "var(--app-text-muted)",
      backgroundColor: active ? "var(--app-bg-surface)" : "transparent",
      transition: "all 0.15s ease",
      userSelect: "none",
      "&:hover": {
        backgroundColor: active ? "var(--app-bg-surface)" : "var(--app-bg-subtle)",
        color: active ? "var(--app-text-primary)" : "var(--app-text-primary)",
      },
    }}
  >
    {children}
  </Box>
);

/**
 * Quality dropdown item
 */
const QualityItem: React.FC<{
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
  isLast?: boolean;
}> = ({ label, description, active, onClick, isLast = false }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      padding: "10px 14px",
      cursor: "pointer",
      backgroundColor: active ? "var(--app-bg-subtle)" : "transparent",
      "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
      borderBottom: !isLast ? "1px solid var(--app-border-subtle)" : "none",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ width: 18, display: "flex", justifyContent: "center" }}>
        {active && <CheckIcon sx={{ fontSize: 16, color: "var(--app-text-primary)" }} />}
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: active ? 500 : 400, color: "var(--app-text-primary)", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)", whiteSpace: "nowrap" }}>
      {description}
    </Typography>
  </Box>
);

const formatDurationLabel = (durationSec: number): string => {
  const total = Math.max(0, Math.round(Number(durationSec) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const compositionHasVisualContent = (): boolean =>
  ScriptLayerStore.hasVideo ||
  Object.values(CompositionStateStore.clips || {}).some((c: any) => {
    const t = String(c?.type ?? "").toLowerCase();
    return t === "video" || t === "image" || t === "captions" || t === "subtitles";
  });

/**
 * ExportCard - Centered modal for export configuration
 * 
 * Design principles (from DesignSystem.txt):
 * - Export is a terminal action requiring intentional pause
 * - Clean, professional, not utility dropdown
 * - Calm, editorial, focused UI
 */
const ExportCard: React.FC<ExportCardProps> = observer(({ 
  onClose, 
  onExport, 
  defaultTitle = "libretto_export", 
  durationSec = 0,
  canvasSize,
  anchorPosition,
  previewThumbnailUrl,
  shouldStartTrialOnExport = false,
  isStartingTrialOnExport = false,
  }) => {
	const [tab, setTab] = React.useState<ExportType>(() =>
	  compositionHasVisualContent() ? "video" : "audio"
	);
	const [filename, setFilename] = React.useState<string>(defaultTitle);
	const fallbackWidth = 1920;
	const fallbackHeight = 1080;
	let cw = canvasSize?.width ?? fallbackWidth;
	let ch = canvasSize?.height ?? fallbackHeight;
	if (!Number.isFinite(cw) || !Number.isFinite(ch) || cw <= 0 || ch <= 0) {
	  cw = fallbackWidth;
	  ch = fallbackHeight;
	}
	const canvasShort = Math.min(cw, ch);
	const normalizeResolutionLabel = React.useCallback((label: string): string => {
	  // Back-compat for an older label we briefly used.
	  if (label === "2160p") return "4k";
	  return label;
	}, []);

	const availableResolutionPresets = React.useMemo(() => {
	  const filtered = RESOLUTION_PRESETS.filter((p) => p.shortSide <= canvasShort);
	  return filtered.length ? filtered : [RESOLUTION_PRESETS[0]];
	}, [canvasShort]);
	const maxAvailableResolutionLabel = React.useMemo(() => {
	  return availableResolutionPresets[availableResolutionPresets.length - 1]?.label ?? RESOLUTION_PRESETS[0].label;
	}, [availableResolutionPresets]);

  const defaultResolutionLabel = React.useMemo(() => {
    return (
      availableResolutionPresets.find((r) => r.label === "1080p")?.label ??
      availableResolutionPresets[availableResolutionPresets.length - 1]?.label ??
      "1080p"
    );
	}, [availableResolutionPresets]);

	const [resolution, setResolution] = React.useState<string>(() => {
	  const stored = normalizeResolutionLabel(ExportStore.selectedResolution);
	  return availableResolutionPresets.some((r) => r.label === stored) ? stored : defaultResolutionLabel;
	});
  const [quality, setQuality] = React.useState<QualityLevel>(ExportStore.selectedQuality);
  const [transcriptOptions, setTranscriptOptions] = React.useState<TranscriptExportOptions>(
    DEFAULT_TRANSCRIPT_EXPORT_OPTIONS
  );
  const [showQualityDropdown, setShowQualityDropdown] = React.useState(false);

  // Ref for click-outside detection
  const qualityDropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (qualityDropdownRef.current && !qualityDropdownRef.current.contains(event.target as Node)) {
        setShowQualityDropdown(false);
      }
    };
    if (showQualityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQualityDropdown]);

  // Sync with store
  React.useEffect(() => {
    ExportStore.setQuality(quality);
  }, [quality]);

  React.useEffect(() => {
    ExportStore.setResolution(resolution);
  }, [resolution]);

  // Ensure we never keep a resolution selection that's unsupported for this canvas.
	React.useEffect(() => {
	  if (availableResolutionPresets.some((r) => r.label === resolution)) return;
	  const stored = normalizeResolutionLabel(ExportStore.selectedResolution);
	  const next = availableResolutionPresets.some((r) => r.label === stored) ? stored : defaultResolutionLabel;
	  setResolution(next);
	}, [availableResolutionPresets, defaultResolutionLabel, resolution, normalizeResolutionLabel]);

  const computeOutputResolution = (shortSide: number): { width: number; height: number } => {
    // Clamp requested short-side to the maximum supported by the composition canvas.
    const safeShortSide = Math.max(2, Math.min(shortSide, Math.min(cw, ch)));

    const roundEven = (value: number): number => {
      const rounded = Math.round(value);
      return rounded % 2 === 0 ? rounded : rounded + 1;
    };

    if (cw === ch) {
      return { width: safeShortSide, height: safeShortSide };
    }

    const canvasShort = Math.min(cw, ch);
    const canvasLong = Math.max(cw, ch);
    const aspect = canvasLong / canvasShort;
    const longSide = Math.max(2, roundEven(safeShortSide * aspect));

    if (cw > ch) {
      // Landscape: short side is height (e.g. 1080p => 1920x1080)
      return { width: longSide, height: safeShortSide };
    }
    // Portrait: short side is width (e.g. 1080p => 1080x1920)
    return { width: safeShortSide, height: longSide };
  };

  const handleExport = () => {
    if (tab === "transcript") {
      void onExport({
        tab,
        filename: filename || defaultTitle,
        transcriptOptions,
      });
      return;
    }

    const resPreset =
      availableResolutionPresets.find((r) => r.label === resolution) ||
      availableResolutionPresets[availableResolutionPresets.length - 1];
    void onExport({
      tab,
      filename: filename || defaultTitle,
      resolution: computeOutputResolution(resPreset.shortSide),
      quality,
    });
  };

  const exportButtonLabel =
    tab === "transcript"
      ? "Export Transcript"
      : isStartingTrialOnExport
      ? "Starting free trial..."
      : shouldStartTrialOnExport
      ? "Export & start free trial"
      : `Export ${tab === "video" ? "Video" : "Audio"}`;

  const qualityPresets = tab === 'video' ? VIDEO_QUALITY_PRESETS : AUDIO_QUALITY_PRESETS;
  const estimatedSize =
    tab === "video" || tab === "audio"
      ? ExportStore.estimateFileSize(durationSec, tab, quality)
      : "—";
  const maxResolutionLabel = maxAvailableResolutionLabel;

  const getQualityDisplay = React.useCallback(
    (key: QualityLevel): { label: string; description: string } => {
      const base = (qualityPresets as any)?.[key] ?? {};
      const rawDesc = String(base.description ?? '').trim();
      if (key === 'high') return { label: 'High quality', description: rawDesc };
      if (key === 'low') return { label: 'Smaller file', description: rawDesc };
      return { label: 'Recommended', description: rawDesc };
    },
    [qualityPresets],
  );

  const durationLabel = formatDurationLabel(durationSec);
  const sizeLabel = tab === "transcript" ? "Text file" : estimatedSize;
  const controlWidthPx = 220;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box
        sx={{
          position: "fixed",
          top: anchorPosition?.top ?? 52,
          left: anchorPosition?.left ?? 8,
          display: "flex",
          flexDirection: "column",
          background: "var(--app-bg-surface)",
          borderRadius: "12px",
          width: "460px",
          maxWidth: "min(460px, calc(100vw - 16px))",
          boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.16)",
          border: "1px solid var(--app-border-subtle)",
          zIndex: 1600,
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.25,
            pt: 1.75,
            pb: 1.25,
            borderBottom: "1px solid var(--app-border-subtle)",
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "var(--app-text-primary)", letterSpacing: "-0.02em" }}>
            Export
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "var(--app-text-muted)",
              padding: "4px",
              "&:hover": { color: "var(--app-text-primary)", backgroundColor: "transparent" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 1.75, pt: 1.25, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "var(--app-bg-subtle)",
              border: "1px solid var(--app-border-subtle)",
              borderRadius: "10px",
              padding: "2px",
              gap: "2px",
              height: 34,
              justifyContent: "space-between",
              mb: 1.8,
            }}
          >
            <SegmentedButton active={tab === "video"} onClick={() => setTab("video")}>
              Video
            </SegmentedButton>
            <SegmentedButton active={tab === "audio"} onClick={() => setTab("audio")}>
              Audio
            </SegmentedButton>
            <SegmentedButton active={tab === "transcript"} onClick={() => setTab("transcript")}>
              Transcript
            </SegmentedButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <Box
                sx={{
                  width: 120,
                  flexShrink: 0,
                  aspectRatio: "16 / 9",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid var(--app-border-subtle)",
                  backgroundColor: "var(--app-bg-subtle)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {previewThumbnailUrl ? (
                  <img
                    src={previewThumbnailUrl}
                    alt="Composition preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <ImageOutlinedIcon sx={{ fontSize: 22, color: "var(--app-text-subtle)" }} />
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Untitled"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontSize: 14,
                      fontWeight: 400,
                      backgroundColor: "var(--app-bg-subtle)",
                      "& fieldset": {
                        borderColor: "var(--app-border-subtle)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--app-border-muted)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2B6BFD",
                        borderWidth: 1,
                      },
                      "& .MuiInputBase-input": {
                        color: "var(--app-text-primary)",
                      },
                    },
                  }}
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: "var(--app-text-muted)" }} />
                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)" }}>{durationLabel}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 14, color: "var(--app-text-muted)" }} />
                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)" }}>{sizeLabel}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {tab === "video" && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Box sx={{ minWidth: 96 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-primary)" }}>
                    Resolution
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "var(--app-text-subtle)", mt: 0.25 }}>
                    Up to {maxResolutionLabel}
                  </Typography>
                </Box>
                <Select
                  size="small"
                  value={resolution}
                  onChange={(e) => setResolution(String(e.target.value))}
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{
                    minWidth: controlWidthPx,
                    borderRadius: "10px",
                    fontSize: 14,
                    fontWeight: 400,
                    backgroundColor: "var(--app-bg-subtle)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--app-border-subtle)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--app-border-muted)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#2B6BFD",
                    },
                    "& .MuiSelect-icon": {
                      color: "var(--app-text-subtle)",
                    },
                    "& .MuiSelect-select": {
                      color: "var(--app-text-primary)",
                    },
                  }}
                  MenuProps={{
                    disablePortal: true,
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: "10px",
                        border: "1px solid var(--app-border-subtle)",
                        boxShadow: "0px 12px 40px rgba(15, 23, 42, 0.12)",
                      },
                    },
                  }}
                >
                  {availableResolutionPresets.map((opt) => (
                    <MenuItem key={opt.label} value={opt.label}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {(tab === "video" || tab === "audio") && (
              <Box
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}
                ref={qualityDropdownRef}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-primary)", minWidth: 96 }}>
                  Quality
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    minWidth: controlWidthPx,
                  }}
                >
                  <Box
                    onClick={() => setShowQualityDropdown(!showQualityDropdown)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "9px 12px",
                      cursor: "pointer",
                      backgroundColor: "var(--app-bg-subtle)",
                      border: "1px solid var(--app-border-subtle)",
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
                      transition: "background-color 0.15s ease",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "var(--app-text-primary)" }}>
                      {getQualityDisplay(quality).label}
                    </Typography>
                    <KeyboardArrowDownIcon
                      sx={{
                        fontSize: 18,
                        color: "var(--app-text-subtle)",
                        transform: showQualityDropdown ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.15s ease",
                      }}
                    />
                  </Box>

                  {showQualityDropdown && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        mt: 0.5,
                        backgroundColor: "var(--app-bg-surface)",
                        border: "1px solid var(--app-border-subtle)",
                        borderRadius: "10px",
                        boxShadow: "0px 12px 40px rgba(15, 23, 42, 0.12)",
                        zIndex: 10,
                        overflow: "hidden",
                      }}
                    >
                      {(Object.keys(qualityPresets) as QualityLevel[]).map((key, index, arr) => (
                        <QualityItem
                          key={key}
                          label={getQualityDisplay(key).label}
                          description={getQualityDisplay(key).description}
                          active={quality === key}
                          isLast={index === arr.length - 1}
                          onClick={() => {
                            setQuality(key);
                            setShowQualityDropdown(false);
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {tab === "transcript" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.1 }}>
                {([
                  { key: "includeMarkers", label: "Include markers" },
                  { key: "includeIgnoredText", label: "Include ignored text" },
                  { key: "includeHighlights", label: "Include highlights" },
                  { key: "includeSpeakerLabels", label: "Include speaker labels" },
                ] as Array<{ key: keyof TranscriptExportOptions; label: string }>).map(({ key, label }) => (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      minHeight: 34,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 350, color: "var(--app-text-primary)" }}>
                      {label}
                    </Typography>
                    <Switch
                      checked={Boolean(transcriptOptions[key])}
                      onChange={(_, checked) =>
                        setTranscriptOptions((prev) => ({
                          ...prev,
                          [key]: checked,
                        }))
                      }
                      sx={{
                        mr: -0.5,
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "var(--app-bg-surface)",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "var(--app-text-primary)",
                          opacity: 1,
                        },
                        "& .MuiSwitch-track": {
                          borderRadius: 999,
                          backgroundColor: "var(--app-border-muted)",
                          opacity: 1,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Button
            data-tour="export-card-submit"
            fullWidth
            variant="contained"
            onClick={handleExport}
            disabled={isStartingTrialOnExport}
            sx={{
              mt: 2.25,
              borderRadius: "10px",
              background: "var(--app-text-primary)",
              color: "var(--app-bg-surface)",
              textTransform: "none",
              fontSize: 14,
              fontWeight: 500,
              py: 1.15,
              boxShadow: "none",
              "&:hover": {
                opacity: 0.92,
                background: "var(--app-text-primary)",
                boxShadow: "none",
              },
              "&:active": {
                opacity: 0.85,
                background: "var(--app-text-primary)",
              },
            }}
          >
            {exportButtonLabel}
          </Button>
        </Box>
      </Box>
    </ClickAwayListener>
  );
});

export default ExportCard;
